// ===== components/ChartRenderer.js =====
// Renders <chart_data> tags as interactive Recharts visualizations

import React, { useMemo } from 'react';
import {
  BarChart, Bar, LineChart, Line, AreaChart, Area,
  PieChart, Pie, Cell, ComposedChart,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, LabelList
} from 'recharts';

// ────────────────────────────────────────────────
// Value Formatters
// ────────────────────────────────────────────────
const formatValue = (value, format, currency = '$') => {
  if (value == null || isNaN(value)) return '';
  const num = Number(value);

  switch (format) {
    case 'currency':
      if (num >= 1e12) return `${currency}${(num / 1e12).toFixed(1)}T`;
      if (num >= 1e9) return `${currency}${(num / 1e9).toFixed(1)}B`;
      if (num >= 1e6) return `${currency}${(num / 1e6).toFixed(1)}M`;
      if (num >= 1e3) return `${currency}${(num / 1e3).toFixed(1)}K`;
      return `${currency}${num.toLocaleString()}`;
    case 'percent':
      return `${num.toFixed(1)}%`;
    case 'compact':
      if (num >= 1e9) return `${(num / 1e9).toFixed(1)}B`;
      if (num >= 1e6) return `${(num / 1e6).toFixed(1)}M`;
      if (num >= 1e3) return `${(num / 1e3).toFixed(1)}K`;
      return num.toLocaleString();
    default:
      return num.toLocaleString();
  }
};

// ────────────────────────────────────────────────
// Custom Tooltip
// ────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label, formatType, currency }) => {
  if (!active || !payload?.length) return null;

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg p-3 text-sm">
      <p className="font-medium text-gray-900 dark:text-white mb-1">{label}</p>
      {payload.map((entry, index) => (
        <p key={index} style={{ color: entry.color }} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: entry.color }} />
          <span>{entry.name}: {formatValue(entry.value, formatType, currency)}</span>
        </p>
      ))}
    </div>
  );
};

// ────────────────────────────────────────────────
// Chart Components
// ────────────────────────────────────────────────
const BarChartComponent = ({ data, config, colors, xAxisKey, yAxisKey, series }) => {
  const formatType = config?.formatYAxis || 'number';
  const currency = config?.currency || '$';

  return (
    <ResponsiveContainer width="100%" height={config?.height || 350}>
      <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
        <XAxis dataKey={xAxisKey} tick={{ fontSize: 12 }} />
        <YAxis tickFormatter={(v) => formatValue(v, formatType, currency)} tick={{ fontSize: 11 }} />
        <Tooltip content={<CustomTooltip formatType={formatType} currency={currency} />} />
        {config?.showLegend !== false && series?.length > 1 && <Legend />}
        {series?.length > 0 ? (
          series.filter(s => !s.type || s.type === 'bar').map((s, i) => (
            <Bar key={s.key} dataKey={s.key} name={s.name || s.key} fill={s.color || colors[i % colors.length]} radius={[4, 4, 0, 0]}>
              {config?.showValues && <LabelList dataKey={s.key} position="top" formatter={(v) => formatValue(v, formatType, currency)} style={{ fontSize: 10 }} />}
            </Bar>
          ))
        ) : (
          <Bar dataKey={yAxisKey} fill={colors[0]} radius={[4, 4, 0, 0]}>
            {data.map((_, index) => (
              <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
            ))}
            {config?.showValues && <LabelList dataKey={yAxisKey} position="top" formatter={(v) => formatValue(v, formatType, currency)} style={{ fontSize: 10 }} />}
          </Bar>
        )}
      </BarChart>
    </ResponsiveContainer>
  );
};

const LineChartComponent = ({ data, config, colors, xAxisKey, yAxisKey, series }) => {
  const formatType = config?.formatYAxis || 'number';
  const currency = config?.currency || '$';

  return (
    <ResponsiveContainer width="100%" height={config?.height || 350}>
      <LineChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
        <XAxis dataKey={xAxisKey} tick={{ fontSize: 12 }} />
        <YAxis tickFormatter={(v) => formatValue(v, formatType, currency)} tick={{ fontSize: 11 }} />
        <Tooltip content={<CustomTooltip formatType={formatType} currency={currency} />} />
        {config?.showLegend !== false && <Legend />}
        {series?.length > 0 ? (
          series.map((s, i) => (
            <Line key={s.key} type="monotone" dataKey={s.key} name={s.name || s.key}
              stroke={s.color || colors[i % colors.length]} strokeWidth={2} dot={{ r: 4 }} />
          ))
        ) : (
          <Line type="monotone" dataKey={yAxisKey} stroke={colors[0]} strokeWidth={2} dot={{ r: 4 }} />
        )}
      </LineChart>
    </ResponsiveContainer>
  );
};

const AreaChartComponent = ({ data, config, colors, xAxisKey, yAxisKey, series }) => {
  const formatType = config?.formatYAxis || 'number';
  const currency = config?.currency || '$';

  return (
    <ResponsiveContainer width="100%" height={config?.height || 350}>
      <AreaChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
        <XAxis dataKey={xAxisKey} tick={{ fontSize: 12 }} />
        <YAxis tickFormatter={(v) => formatValue(v, formatType, currency)} tick={{ fontSize: 11 }} />
        <Tooltip content={<CustomTooltip formatType={formatType} currency={currency} />} />
        {config?.showLegend !== false && <Legend />}
        {series?.length > 0 ? (
          series.map((s, i) => (
            <Area key={s.key} type="monotone" dataKey={s.key} name={s.name || s.key}
              stroke={s.color || colors[i % colors.length]}
              fill={s.color || colors[i % colors.length]} fillOpacity={0.3} />
          ))
        ) : (
          <Area type="monotone" dataKey={yAxisKey} stroke={colors[0]} fill={colors[0]} fillOpacity={0.3} />
        )}
      </AreaChart>
    </ResponsiveContainer>
  );
};

const PieChartComponent = ({ data, config, colors }) => {
  const formatType = config?.formatYAxis || 'number';
  const currency = config?.currency || '$';

  return (
    <ResponsiveContainer width="100%" height={config?.height || 350}>
      <PieChart>
        <Pie data={data} cx="50%" cy="50%" outerRadius={120} innerRadius={60}
          dataKey="value" nameKey="name" paddingAngle={2}>
          {data.map((_, index) => (
            <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
          ))}
          <LabelList dataKey="name" position="outside" style={{ fontSize: 11 }} />
        </Pie>
        <Tooltip content={<CustomTooltip formatType={formatType} currency={currency} />} />
        <Legend />
      </PieChart>
    </ResponsiveContainer>
  );
};

const ComposedChartComponent = ({ data, config, colors, xAxisKey, series }) => {
  const formatType = config?.formatYAxis || 'number';
  const currency = config?.currency || '$';

  return (
    <ResponsiveContainer width="100%" height={config?.height || 350}>
      <ComposedChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
        <XAxis dataKey={xAxisKey} tick={{ fontSize: 12 }} />
        <YAxis tickFormatter={(v) => formatValue(v, formatType, currency)} tick={{ fontSize: 11 }} />
        <Tooltip content={<CustomTooltip formatType={formatType} currency={currency} />} />
        <Legend />
        {series?.map((s, i) => {
          const color = s.color || colors[i % colors.length];
          switch (s.type) {
            case 'line':
              return <Line key={s.key} type="monotone" dataKey={s.key} name={s.name || s.key} stroke={color} strokeWidth={2} dot={{ r: 4 }} />;
            case 'area':
              return <Area key={s.key} type="monotone" dataKey={s.key} name={s.name || s.key} stroke={color} fill={color} fillOpacity={0.3} />;
            default:
              return <Bar key={s.key} dataKey={s.key} name={s.name || s.key} fill={color} radius={[4, 4, 0, 0]} />;
          }
        })}
      </ComposedChart>
    </ResponsiveContainer>
  );
};

const FunnelChartComponent = ({ data, config, colors }) => {
  const currency = config?.currency || '$';

  return (
    <div className="space-y-2" style={{ minHeight: config?.height || 250 }}>
      {data.map((item, index) => {
        const maxValue = data[0]?.value || 1;
        const widthPercent = Math.max((item.value / maxValue) * 100, 20);
        const color = colors[index % colors.length];

        return (
          <div key={item.name} className="flex flex-col items-center">
            <div
              className="rounded-lg py-3 px-4 text-white font-medium text-center transition-all duration-300 hover:opacity-90 shadow-md"
              style={{
                width: `${widthPercent}%`,
                backgroundColor: color,
                minWidth: '200px',
              }}
            >
              <div className="text-lg font-bold">{item.displayValue || formatValue(item.value, 'currency', currency)}</div>
              <div className="text-sm opacity-90">{item.fullName || item.name}</div>
              {item.percentOfTam && (
                <div className="text-xs opacity-75 mt-1">
                  {item.percentOfTam}% of TAM
                  {item.percentOfSam && ` · ${item.percentOfSam}% of SAM`}
                </div>
              )}
            </div>
            {index < data.length - 1 && (
              <div className="text-gray-400 dark:text-gray-500 text-lg">▼</div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const DataTableComponent = ({ data, config }) => {
  const { headers = [], rows = [] } = data || {};
  const highlightCol = config?.highlightColumn;
  const highlightIdx = highlightCol ? headers.indexOf(highlightCol) : -1;

  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-600">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 dark:bg-gray-700">
            {headers.map((h, i) => (
              <th key={i} className={`px-4 py-2.5 text-left font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-600 ${i === highlightIdx ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIdx) => (
            <tr key={rowIdx} className={`${config?.striped && rowIdx % 2 === 1 ? 'bg-gray-50/50 dark:bg-gray-800/50' : ''} hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors`}>
              {row.map((cell, cellIdx) => (
                <td key={cellIdx} className={`px-4 ${config?.compact ? 'py-1.5' : 'py-2.5'} text-gray-700 dark:text-gray-300 border-b border-gray-100 dark:border-gray-700 ${cellIdx === highlightIdx ? 'font-semibold text-blue-600 dark:text-blue-400 bg-blue-50/50 dark:bg-blue-900/10' : ''}`}>
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ────────────────────────────────────────────────
// Main ChartRenderer Component
// ────────────────────────────────────────────────
const ChartRenderer = ({ chartJson }) => {
  const config = useMemo(() => {
    if (!chartJson) return null;
    try {
      return typeof chartJson === 'string' ? JSON.parse(chartJson) : chartJson;
    } catch (e) {
      console.error('Failed to parse chart config:', e);
      return null;
    }
  }, [chartJson]);

  if (!config) return null;

  const {
    chartType, title, subtitle, data, xAxisKey = 'name',
    yAxisKey = 'value', colors = ['#3B82F6'], series,
    config: chartConfig = {}, source, fallbackChart
  } = config;

  const chartComponents = {
    bar: BarChartComponent,
    horizontal_bar: BarChartComponent,
    line: LineChartComponent,
    area: AreaChartComponent,
    pie: PieChartComponent,
    stacked_bar: BarChartComponent,
    composed: ComposedChartComponent,
    funnel: FunnelChartComponent,
    table: DataTableComponent,
  };

  const ChartComponent = chartComponents[chartType];

  if (!ChartComponent) {
    console.warn(`Unsupported chart type: ${chartType}`);
    if (fallbackChart) {
      return <ChartRenderer chartJson={fallbackChart} />;
    }
    return null;
  }

  // In ChartRenderer.js — only change is the wrapper div
return (
    <div 
      className="my-4 p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-600 shadow-sm"
      data-chart-container="true"
    >
      {title && (
        <div className="mb-3">
          <h4 className="text-base font-semibold text-gray-900 dark:text-white">{title}</h4>
          {subtitle && <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{subtitle}</p>}
        </div>
      )}

      <ChartComponent
        data={data}
        config={chartConfig}
        colors={colors}
        xAxisKey={xAxisKey}
        yAxisKey={yAxisKey}
        series={series}
      />

      {source && (
        <p className="mt-2 text-xs text-gray-400 dark:text-gray-500 italic">
          Source: {source}
        </p>
      )}
    </div>
  );
};

export default ChartRenderer;